import React from 'react';
import { Search, Package, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PromocaoCard } from './PromocaoCard';
import { PaginacaoControls } from './PaginacaoControls';
import type { Promocao } from '../types/multipatrocinadores';


type PromocaoListProps = {
  promocoes: Promocao[];
  promocoesSelecionadas: string[];
  loading: boolean;
  pagination: any;
  onSelecionarPromocao: (promocao: Promocao) => void;
  onPaginaChange: (page: number) => void;
  onViewHistory?: (idRegraPromocao: string) => void;
};

export const PromocaoList: React.FC<PromocaoListProps> = ({
  promocoes,
  promocoesSelecionadas,
  loading,
  pagination,
  onSelecionarPromocao,
  onPaginaChange,
  onViewHistory,
}) => {
  const paginaAtual = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPaginas = Math.ceil(pagination.total / pagination.limit);

  const isPromocaoSelecionada = (promocaoId:any) => {
    return promocoesSelecionadas.includes(promocaoId);
  };

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <h2 className="text-lg sm:text-xl font-bold text-slate-900">Promoções Disponíveis</h2>
          <span className="text-xs sm:text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">Carregando...</span>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-slate-200 p-8 sm:p-12 text-center"
        >
          <div className="flex flex-col items-center space-y-4">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border-4 border-blue-200 border-t-blue-600"
            />
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-2">Carregando promoções...</h3>
              <p className="text-sm sm:text-base text-slate-500">
                Aguarde enquanto buscamos as promoções disponíveis
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  if (promocoes.length === 0 && !loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <h2 className="text-lg sm:text-xl font-bold text-slate-900">Promoções Disponíveis</h2>
          <span className="text-xs sm:text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">0 promoções</span>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-br from-slate-50 to-white rounded-xl sm:rounded-2xl shadow-lg border border-slate-200 p-8 sm:p-12 text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-inner"
          >
            <Search className="h-8 w-8 sm:h-10 sm:w-10 text-slate-400" />
          </motion.div>
          <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-2">Nenhuma promoção encontrada</h3>
          <p className="text-sm sm:text-base text-slate-500">
            Tente ajustar os filtros para encontrar as promoções desejadas
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
            <Package className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-slate-900">Promoções Disponíveis</h2>
        </div>
        <motion.span 
          
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="text-xs sm:text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-200"
        >
          {pagination.total.toLocaleString()} {pagination.total === 1 ? 'promoção' : 'promoções'}
        </motion.span>
      </motion.div>

      {/* Paginação Superior */}
      {totalPaginas > 1 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <PaginacaoControls
            paginaAtual={paginaAtual}
            totalPaginas={totalPaginas}
            totalItens={pagination.total}
            itensPorPagina={pagination.limit}
            onPaginaChange={(pagina) => onPaginaChange((pagina - 1) * pagination.limit)}
            variant="completa"
          />
        </motion.div>
      )}

      {/* Lista de Promoções */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="bg-white/50 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-slate-200 p-4 sm:p-6"
      >
        <div className="max-h-[calc(100vh-400px)] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent">
          <AnimatePresence>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
              {promocoes.map((promocao:any, index:any) => (
                <motion.div
                  key={promocao.IDRegraPromocao}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                  className="h-fit"
                >
                  <PromocaoCard
                    promocao={promocao}
                    selecionada={isPromocaoSelecionada(promocao.IDRegraPromocao)}
                    onSelecionar={onSelecionarPromocao}
                    onViewHistory={onViewHistory}
                  />
                </motion.div>
              ))}
            </div>
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Paginação Inferior */}
      {totalPaginas > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <PaginacaoControls
            paginaAtual={paginaAtual}
            totalPaginas={totalPaginas}
            totalItens={pagination.total}
            itensPorPagina={pagination.limit}
            onPaginaChange={(pagina) => onPaginaChange((pagina - 1) * pagination.limit)}
            variant="simples"
          />
        </motion.div>
      )}
    </div>
  );
};
